import React, { useRef, useState, useCallback, useEffect } from "react";
import api from "../services/axiosInstance";

function parseVer(str) {
  const m = String(str || "").match(/(\d+)\.(\d+)\.(\d+)(?:\.(\d+))?/);
  return m ? [+m[1], +m[2], +m[3], +(m[4] ?? 0)] : null;
}

function extractVersion(filename) {
  const m = String(filename).match(/v?(\d+\.\d+\.\d+(?:\.\d+)?)/i);
  return m ? `v${m[1]}` : null;
}

function ReleaseCard({ label, softwareKey, pkg, onUpload, environments }) {
  const inputRef = useRef(null);
  const [targetEnv, setTargetEnv] = useState(environments && environments.length > 0 ? environments[0] : "Live");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [major, setMajor] = useState(1);
  const [minor, setMinor] = useState(0);
  const [patch, setPatch] = useState(0);
  const [build, setBuild] = useState(0);
  const selectedVer = `v${major}.${minor}.${patch}.${build}`;
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadState, setUploadState] = useState("idle"); // idle | uploading | done | error
  const userEditedCardRef = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pkg && pkg.version && !userEditedCardRef.current) {
      const parsed = parseVer(pkg.version);
      if (parsed) {
        setMajor(parsed[0]);
        setMinor(parsed[1]);
        setPatch(parsed[2]);
        setBuild(parsed[3]);
      }
    }
  }, [pkg]);
 
  const handleFile = useCallback((file) => {
    if (!file) return;
    setSelectedFile(file);
    setFileName(file.name);
 
    const detected = extractVersion(file.name);
    if (detected) {
      const parsed = parseVer(detected);
      if (parsed) {
        setMajor(parsed[0]);
        setMinor(parsed[1]);
        setPatch(parsed[2]);
        setBuild(parsed[3]);
      }
    } else {
      setBuild(b => b + 1);
    }
  }, []);

  const handleBrowse = () => inputRef.current?.click();

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile && !fileName) return;
    setUploadState("uploading");
    try {
      await onUpload(selectedFile, softwareKey, selectedVer, fileName, targetEnv);
      setUploadState("done");
      setTimeout(() => setUploadState("idle"), 2500);
    } catch {
      setUploadState("error");
      setTimeout(() => setUploadState("idle"), 2500);
    }
  };

  const isUploading = uploadState === "uploading";
  const hasPackage  = !!pkg;

  return (
    <div className={`rc-card${isDragOver ? " rc-card--drag" : ""}`} style={{ borderLeft: "none", background: "var(--accent-gradient)" }}>
      {/* Card header */}
      <div className="rc-header">
        <div className="rc-header-label">
          <span className="rc-icon" aria-hidden="true" style={{ color: "#fff" }}>
            {softwareKey === "HeadOfficeV2" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            ) : softwareKey === "ServerService" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                <line x1="6" y1="6" x2="6.01" y2="6" />
                <line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            )}
          </span>
          <span className="rc-title" style={{ fontWeight: "700" }}>{label}</span>
        </div>
        {hasPackage ? (
          <span className="rc-badge rc-badge--active" style={{ background: "rgba(34,197,94,0.15)", color: "#52c48d", border: "1px solid rgba(34,197,94,0.3)" }}>{pkg.version}</span>
        ) : (
          <span className="rc-badge rc-badge--none" style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)" }}>No package</span>
        )}
      </div>

      {/* Modern Drag and Drop Zone */}
      <div
        className="rc-dropzone"
        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={handleBrowse}
        style={{
          border: isDragOver ? "2px dashed #748ffc" : "2px dashed rgba(255, 255, 255, 0.25)",
          borderRadius: "var(--radius-md)",
          padding: "20px 14px",
          textAlign: "center",
          cursor: "pointer",
          background: isDragOver ? "rgba(116, 143, 252, 0.1)" : "rgba(255, 255, 255, 0.03)",
          transition: "all var(--transition-fast)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          marginTop: "6px"
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <div style={{ fontSize: "11px", fontWeight: "600", color: "#fff" }}>
          {fileName ? fileName : "Drag & drop package or click to browse"}
        </div>
        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.5)" }}>
          Supports .zip, .msi, .exe binaries
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".zip,.msi,.exe"
        style={{ display: "none" }}
        onChange={e => e.target.files[0] && handleFile(e.target.files[0])}
      />

      {/* Paste path row */}
      <div className="rc-row rc-row--file" style={{ marginTop: "4px" }}>
        <input
          type="text"
          className="rc-file-text"
          value={fileName}
          onChange={e => {
            setFileName(e.target.value);
            setSelectedFile(null);
          }}
          placeholder="Or paste absolute local package path..."
          style={{
            background: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "var(--radius-sm)",
            color: "#fff",
            padding: "6px 10px",
            fontSize: "11px",
            outline: "none",
            width: "100%",
            boxSizing: "border-box"
          }}
        />
      </div>

      {/* Target Environment row */}
      <div className="rc-row rc-row--version" style={{ marginTop: "6px" }}>
        <span className="rc-label-sm" style={{ width: "auto", marginRight: "8px" }}>Target Type:</span>
        <select className="toolbar-select" style={{ padding: "4px 8px", fontSize: "11px", flex: 1 }} value={targetEnv} onChange={e => setTargetEnv(e.target.value)}>
           {(environments || []).map(e => <option key={e} value={e}>{e}</option>)}
        </select>
      </div>

      {/* Version row */}
      <div className="rc-row rc-row--version" style={{ marginTop: "6px" }}>
        <span className="rc-label-sm" style={{ width: "auto", marginRight: "8px" }}>Version:</span>
        <div className="rc-version-control">
          <div className="rc-version-inputs" style={{ background: "rgba(255, 255, 255, 0.08)", border: "1px solid rgba(255, 255, 255, 0.12)" }}>
            <span className="rc-version-prefix" style={{ color: "rgba(255, 255, 255, 0.7)" }}>v</span>
            <input
              type="number"
              className="rc-version-num"
              min="0"
              value={major}
              onChange={e => { userEditedCardRef.current = true; setMajor(Math.max(0, parseInt(e.target.value) || 0)); }}
              style={{ color: "#fff", background: "transparent", border: "none", width: "32px", fontSize: "11px", outline: "none", textAlign: "center" }}
            />
            <span className="rc-version-dot" style={{ color: "rgba(255, 255, 255, 0.5)" }}>.</span>
            <input
              type="number"
              className="rc-version-num"
              min="0"
              value={minor}
              onChange={e => { userEditedCardRef.current = true; setMinor(Math.max(0, parseInt(e.target.value) || 0)); }}
              style={{ color: "#fff", background: "transparent", border: "none", width: "32px", fontSize: "11px", outline: "none", textAlign: "center" }}
            />
            <span className="rc-version-dot" style={{ color: "rgba(255, 255, 255, 0.5)" }}>.</span>
            <input
              type="number"
              className="rc-version-num"
              min="0"
              value={patch}
              onChange={e => { userEditedCardRef.current = true; setPatch(Math.max(0, parseInt(e.target.value) || 0)); }}
              style={{ color: "#fff", background: "transparent", border: "none", width: "28px", fontSize: "11px", outline: "none", textAlign: "center" }}
            />
            <span className="rc-version-dot" style={{ color: "rgba(255, 255, 255, 0.5)" }}>.</span>
            <input
              type="number"
              className="rc-version-num"
              min="0"
              value={build}
              onChange={e => { userEditedCardRef.current = true; setBuild(Math.max(0, parseInt(e.target.value) || 0)); }}
              style={{ color: "#fff", background: "transparent", border: "none", width: "28px", fontSize: "11px", outline: "none", textAlign: "center" }}
            />
          </div>

          <div className="rc-action-group">
            <button
              className={`rc-btn rc-btn--upload${isUploading ? " rc-btn--loading" : ""}${uploadState === "done" ? " rc-btn--done" : ""}`}
              disabled={(!selectedFile && !fileName) || isUploading}
              onClick={handleUpload}
              style={{
                fontSize: "11px",
                padding: "6px 12px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              {isUploading ? (
                <>
                  <span className="rc-spinner" aria-hidden="true" />
                  Uploading…
                </>
              ) : uploadState === "done" ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Uploaded
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="rc-divider" style={{ background: "rgba(255, 255, 255, 0.12)", height: "1px", margin: "10px 0" }} />

      {/* Installed package info */}
      {hasPackage && (
        <div className="rc-pkg-info" style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "rgba(255, 255, 255, 0.6)" }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <span className="rc-pkg-name" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{pkg.name}</span>
          <span className="rc-pkg-size">{(pkg.size / 1024 / 1024).toFixed(1)} MB</span>
        </div>
      )}
    </div>
  );
}

function PromoteCard({ label, softwareKey, environments }) {
  const [sourceEnv, setSourceEnv] = useState("Demo");
  const [targetEnv, setTargetEnv] = useState("Live");
  const [fromVer, setFromVer] = useState({ major: 1, minor: 0, patch: 0, build: 0 });
  const [toVer, setToVer] = useState({ major: 1, minor: 0, patch: 0, build: 0 });

  return (
    <div className="rc-card" style={{ borderLeft: "none", background: "var(--accent-gradient)" }}>
      {/* Card header */}
      <div className="rc-header">
        <div className="rc-header-label">
          <span className="rc-icon" aria-hidden="true" style={{ color: "#fff" }}>
            {softwareKey === "HeadOfficeV2" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            ) : softwareKey === "ServerService" ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
                <line x1="6" y1="6" x2="6.01" y2="6" />
                <line x1="6" y1="18" x2="6.01" y2="18" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            )}
          </span>
          <span className="rc-title" style={{ fontWeight: "700" }}>{label}</span>
        </div>
      </div>

      <div style={{ marginTop: "12px", background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span className="rc-label-sm" style={{ fontWeight: "600", color: "#748ffc" }}>From:</span>
          <select className="toolbar-select" style={{ padding: "4px 8px", fontSize: "11px" }} value={sourceEnv} onChange={e => setSourceEnv(e.target.value)}>
             {environments.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="rc-version-inputs" style={{ background: "rgba(255, 255, 255, 0.08)", border: "1px solid rgba(255, 255, 255, 0.12)", justifyContent: "center", padding: "4px 0" }}>
          <span className="rc-version-prefix">v</span>
          <input type="number" className="rc-version-num" min="0" value={fromVer.major} onChange={e => setFromVer({...fromVer, major: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={fromVer.minor} onChange={e => setFromVer({...fromVer, minor: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={fromVer.patch} onChange={e => setFromVer({...fromVer, patch: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={fromVer.build} onChange={e => setFromVer({...fromVer, build: e.target.value})} />
        </div>
      </div>

      <div style={{ textAlign: "center", margin: "8px 0", color: "rgba(255,255,255,0.4)" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>
      </div>

      <div style={{ background: "rgba(255, 255, 255, 0.03)", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span className="rc-label-sm" style={{ fontWeight: "600", color: "#52c48d" }}>To:</span>
          <select className="toolbar-select" style={{ padding: "4px 8px", fontSize: "11px" }} value={targetEnv} onChange={e => setTargetEnv(e.target.value)}>
             {environments.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="rc-version-inputs" style={{ background: "rgba(255, 255, 255, 0.08)", border: "1px solid rgba(255, 255, 255, 0.12)", justifyContent: "center", padding: "4px 0" }}>
          <span className="rc-version-prefix">v</span>
          <input type="number" className="rc-version-num" min="0" value={toVer.major} onChange={e => setToVer({...toVer, major: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={toVer.minor} onChange={e => setToVer({...toVer, minor: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={toVer.patch} onChange={e => setToVer({...toVer, patch: e.target.value})} />
          <span className="rc-version-dot">.</span>
          <input type="number" className="rc-version-num" min="0" value={toVer.build} onChange={e => setToVer({...toVer, build: e.target.value})} />
        </div>
      </div>

      <div style={{ marginTop: "16px" }}>
         <button className="rc-btn rc-btn--deploy" style={{ width: "100%", justifyContent: "center", opacity: sourceEnv === targetEnv ? 0.5 : 1 }} disabled={sourceEnv === targetEnv} onClick={() => alert("Promote API not yet implemented")}>
            Promote {label}
         </button>
      </div>
    </div>
  );
}

export default function ReleasePanel({ hoPkg, dsPkg, ssPkg, onUpload, onDeploy, onDeployGroup, onRollback, groups }) {
  const [releaseAction, setReleaseAction] = useState("upload"); // "upload" | "promote"
  const [serverTypes, setServerTypes] = useState([]);

  useEffect(() => {
    api.get("/servertypes")
      .then(res => {
        setServerTypes(res.data || []);
      })
      .catch(err => console.error("Failed to load server types", err));
  }, []);

  const environments = serverTypes.length > 0 ? serverTypes.map(st => st.name) : ["Live"];

  // Checkboxes selection state
  const [selectedComponents, setSelectedComponents] = useState({
    HeadOfficeV2: false,
    DataServices: false,
    ServerService: false
  });

  // Target versions for each component in the toolbar
  const [versions, setVersions] = useState({
    HeadOfficeV2: { major: 1, minor: 0, patch: 0, build: 0 },
    DataServices: { major: 1, minor: 0, patch: 0, build: 0 },
    ServerService: { major: 1, minor: 0, patch: 0, build: 0 }
  });

  const userEditedRef = useRef({
    HeadOfficeV2: false,
    DataServices: false,
    ServerService: false
  });

  const [deployScope, setDeployScope] = useState("all"); // all | group
  const [selectedGroup, setSelectedGroup] = useState("");
  const [rollbackMode, setRollbackMode] = useState("local"); // local | server
  const [confirmAction, setConfirmAction] = useState(null); // "deploy" | "rollback" | null

  // Sync versions whenever package version updates (only if user has not typed custom values)
  useEffect(() => {
    setVersions(prev => {
      const next = { ...prev };
      let updated = false;
      if (hoPkg?.version && !userEditedRef.current.HeadOfficeV2) {
        const parsed = parseVer(hoPkg.version);
        if (parsed) { next.HeadOfficeV2 = { major: parsed[0], minor: parsed[1], patch: parsed[2], build: parsed[3] }; updated = true; }
      }
      if (dsPkg?.version && !userEditedRef.current.DataServices) {
        const parsed = parseVer(dsPkg.version);
        if (parsed) { next.DataServices = { major: parsed[0], minor: parsed[1], patch: parsed[2], build: parsed[3] }; updated = true; }
      }
      if (ssPkg?.version && !userEditedRef.current.ServerService) {
        const parsed = parseVer(ssPkg.version);
        if (parsed) { next.ServerService = { major: parsed[0], minor: parsed[1], patch: parsed[2], build: parsed[3] }; updated = true; }
      }
      return updated ? next : prev;
    });
  }, [hoPkg?.version, dsPkg?.version, ssPkg?.version]);

  useEffect(() => {
    if (groups.length > 0 && !selectedGroup) {
      setSelectedGroup(groups[0]);
    }
  }, [groups, selectedGroup]);

  const handleVersionChange = (software, field, value) => {
    userEditedRef.current[software] = true;
    const intVal = Math.max(0, parseInt(value) || 0);
    setVersions(prev => ({
      ...prev,
      [software]: {
        ...prev[software],
        [field]: intVal
      }
    }));
  };

  const handleAction = async (action) => {
    const activeKeys = Object.keys(selectedComponents).filter(k => selectedComponents[k]);
    if (activeKeys.length === 0) {
      setConfirmAction(null);
      return;
    }

    if (confirmAction === action) {
      for (const key of activeKeys) {
        const v = versions[key];
        const selectedVer = `v${v.major}.${v.minor}.${v.patch}.${v.build}`;
        if (action === "deploy") {
          if (deployScope === "group" && selectedGroup) {
            await onDeployGroup(selectedGroup, key, selectedVer);
          } else {
            await onDeploy(key, selectedVer);
          }
        } else if (action === "rollback") {
          await onRollback(key, rollbackMode);
        }
      }
      setConfirmAction(null);
    } else {
      setConfirmAction(action);
    }
  };

  const cancelConfirm = () => setConfirmAction(null);

  // Derive selections state
  const activeKeys = Object.keys(selectedComponents).filter(k => selectedComponents[k]);
  const hasSelection = activeKeys.length > 0;

  // Check if every selected component actually has an uploaded package
  const hasPackageForSelected = activeKeys.every(key => {
    if (key === "HeadOfficeV2") return !!hoPkg;
    if (key === "DataServices") return !!dsPkg;
    if (key === "ServerService") return !!ssPkg;
    return false;
  });

  const getConfirmMessage = (action) => {
    const componentNames = activeKeys.map(k => {
      const v = versions[k];
      const name = k === "HeadOfficeV2" ? "HeadOffice" : k === "ServerService" ? "Server Service" : k;
      return `${name} (v${v.major}.${v.minor}.${v.patch}.${v.build})`;
    }).join(", ");
    
    if (action === "deploy") {
      const target = deployScope === "group" ? `group ${selectedGroup}` : "all servers";
      return `Deploy ${componentNames} to ${target}?`;
    }
    if (action === "rollback") {
      return `Rollback ${componentNames} (${rollbackMode === "local" ? "Local" : "SFTP"})?`;
    }
    return "";
  };

  return (
    <section className="portal-panel">
      <div className="portal-panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h2 className="portal-panel-title">Package Releases</h2>
          <span className="portal-panel-hint">Manage software packages across environments</span>
        </div>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <span className="toolbar-label" style={{ fontWeight: "600" }}>Action:</span>
          <select 
            className="toolbar-select" 
            value={releaseAction} 
            onChange={e => setReleaseAction(e.target.value)}
          >
            <option value="upload">Upload Package</option>
            <option value="promote">Promote Package</option>
          </select>
        </div>
      </div>

      {releaseAction === "promote" ? (
        <div className="rc-grid" style={{ marginBottom: "24px" }}>
          <PromoteCard label="HeadOffice" softwareKey="HeadOfficeV2" environments={environments} />
          <PromoteCard label="DataServices" softwareKey="DataServices" environments={environments} />
          <PromoteCard label="Server Service" softwareKey="ServerService" environments={environments} />
        </div>
      ) : (
        <div className="rc-grid" style={{ marginBottom: "24px" }}>
          <ReleaseCard
            label="HeadOffice"
            softwareKey="HeadOfficeV2"
            pkg={hoPkg}
            onUpload={onUpload}
            environments={environments}
          />
          <ReleaseCard
            label="DataServices"
            softwareKey="DataServices"
            pkg={dsPkg}
            onUpload={onUpload}
            environments={environments}
          />
          <ReleaseCard
            label="Server Service"
            softwareKey="ServerService"
            pkg={ssPkg}
            onUpload={onUpload}
            environments={environments}
          />
        </div>
      )}

      {/* Toolbar for Deployments & Rollbacks */}
      <div className="release-toolbar" style={{ flexDirection: "column", alignItems: "stretch", gap: "16px", opacity: releaseAction === "promote" ? 0.4 : 1, pointerEvents: releaseAction === "promote" ? "none" : "auto", transition: "opacity 0.2s" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
          
          <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", flex: 1 }}>
            {/* HeadOffice */}
            <div className="toolbar-checkbox-item">
              <label className="toolbar-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedComponents.HeadOfficeV2}
                  onChange={e => {
                    setSelectedComponents(prev => ({ ...prev, HeadOfficeV2: e.target.checked }));
                    setConfirmAction(null);
                  }}
                />
                <span className="checkbox-text">HeadOffice</span>
              </label>
              
              <div className="rc-version-inputs" style={{ 
                padding: "3px 6px",
                opacity: selectedComponents.HeadOfficeV2 ? 1 : 0.5, 
                pointerEvents: selectedComponents.HeadOfficeV2 ? "auto" : "none",
                transition: "opacity 0.2s"
              }}>
                <span className="rc-version-prefix">v</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.HeadOfficeV2.major}
                  onChange={e => handleVersionChange("HeadOfficeV2", "major", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.HeadOfficeV2.minor}
                  onChange={e => handleVersionChange("HeadOfficeV2", "minor", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.HeadOfficeV2.patch}
                  onChange={e => handleVersionChange("HeadOfficeV2", "patch", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.HeadOfficeV2.build}
                  onChange={e => handleVersionChange("HeadOfficeV2", "build", e.target.value)}
                />
              </div>
            </div>

            {/* DataServices */}
            <div className="toolbar-checkbox-item">
              <label className="toolbar-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedComponents.DataServices}
                  onChange={e => {
                    setSelectedComponents(prev => ({ ...prev, DataServices: e.target.checked }));
                    setConfirmAction(null);
                  }}
                />
                <span className="checkbox-text">DataServices</span>
              </label>
              
              <div className="rc-version-inputs" style={{ 
                padding: "3px 6px",
                opacity: selectedComponents.DataServices ? 1 : 0.5, 
                pointerEvents: selectedComponents.DataServices ? "auto" : "none",
                transition: "opacity 0.2s"
              }}>
                <span className="rc-version-prefix">v</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.DataServices.major}
                  onChange={e => handleVersionChange("DataServices", "major", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.DataServices.minor}
                  onChange={e => handleVersionChange("DataServices", "minor", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.DataServices.patch}
                  onChange={e => handleVersionChange("DataServices", "patch", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.DataServices.build}
                  onChange={e => handleVersionChange("DataServices", "build", e.target.value)}
                />
              </div>
            </div>

            {/* Server Service */}
            <div className="toolbar-checkbox-item">
              <label className="toolbar-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedComponents.ServerService}
                  onChange={e => {
                    setSelectedComponents(prev => ({ ...prev, ServerService: e.target.checked }));
                    setConfirmAction(null);
                  }}
                />
                <span className="checkbox-text">Server Service</span>
              </label>
              
              <div className="rc-version-inputs" style={{ 
                padding: "3px 6px",
                opacity: selectedComponents.ServerService ? 1 : 0.5, 
                pointerEvents: selectedComponents.ServerService ? "auto" : "none",
                transition: "opacity 0.2s"
              }}>
                <span className="rc-version-prefix">v</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.ServerService.major}
                  onChange={e => handleVersionChange("ServerService", "major", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.ServerService.minor}
                  onChange={e => handleVersionChange("ServerService", "minor", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.ServerService.patch}
                  onChange={e => handleVersionChange("ServerService", "patch", e.target.value)}
                />
                <span className="rc-version-dot">.</span>
                <input
                  type="number"
                  className="rc-version-num"
                  min="0"
                  value={versions.ServerService.build}
                  onChange={e => handleVersionChange("ServerService", "build", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Deploy Scope & Actions Row */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center", borderTop: "1px solid var(--border-color)", paddingTop: "12px" }}>
          <div className="toolbar-group">
            <span className="toolbar-label">Scope:</span>
            <select
              className="toolbar-select"
              style={{ minWidth: "120px" }}
              value={deployScope}
              onChange={e => {
                setDeployScope(e.target.value);
                setConfirmAction(null);
              }}
            >
              <option value="all">All Servers</option>
              {groups.length > 0 && <option value="group">By Group</option>}
            </select>
          </div>

          {deployScope === "group" && groups.length > 0 && (
            <div className="toolbar-group animate-fade-in">
              <span className="toolbar-label">Group:</span>
              <select
                className="toolbar-select"
                style={{ minWidth: "130px" }}
                value={selectedGroup}
                onChange={e => {
                  setSelectedGroup(e.target.value);
                  setConfirmAction(null);
                }}
              >
                {groups.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          )}

          <div className="toolbar-group toolbar-group--actions">
            {confirmAction === "deploy" ? (
              <div className="rc-confirm">
                <span className="rc-confirm-text" style={{ fontSize: "11px", fontWeight: "600", marginRight: "4px" }}>
                  {getConfirmMessage("deploy")}
                </span>
                <button className="rc-btn rc-btn--confirm-yes" onClick={() => handleAction("deploy")}>Yes</button>
                <button className="rc-btn rc-btn--cancel" onClick={cancelConfirm}>Cancel</button>
              </div>
            ) : confirmAction === "rollback" ? (
              <div className="rc-confirm" style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span className="rc-confirm-text" style={{ fontSize: "11px", color: "var(--brand-red)", fontWeight: "600", marginRight: "4px" }}>
                  {getConfirmMessage("rollback")}
                </span>
                <select
                  className="toolbar-select"
                  style={{
                    padding: "4px 8px",
                    fontSize: "11px",
                    minWidth: "120px"
                  }}
                  value={rollbackMode}
                  onChange={e => setRollbackMode(e.target.value)}
                >
                  <option value="local">Local Backup (.zip)</option>
                  <option value="server">Server Package (SFTP)</option>
                </select>
                <button className="rc-btn rc-btn--confirm-warn" style={{ padding: "4px 8px" }} onClick={() => handleAction("rollback")}>Yes</button>
                <button className="rc-btn rc-btn--cancel" style={{ padding: "4px 8px" }} onClick={cancelConfirm}>Cancel</button>
              </div>
            ) : (
              <>
                <button
                  className="rc-btn rc-btn--deploy"
                  disabled={!hasSelection || !hasPackageForSelected}
                  onClick={() => handleAction("deploy")}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  Deploy
                </button>
                <button
                  className="rc-btn rc-btn--rollback"
                  disabled={!hasSelection}
                  onClick={() => handleAction("rollback")}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2.5 2v6h6M2.66 15.57a10 10 0 1 0 .57-4.1" />
                  </svg>
                  Rollback
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
